import { navigate } from 'gatsby';
import { Machine, assign } from 'xstate';
import qs from 'qs';

import useLinkCreator from 'hooks/useLinkCreator';
import menuLinks from 'settings/menuLinks';

export type QuizQuestion = {
  id: string;
  question: string;
  answers: string[];
  correctAnswerIndex: number;
};

export type QuizAnswers = {
  [index: string]: number | null;
};

export type QuizMachineContext = {
  currentQuestionIndex: number;
  questions: QuizQuestion[];
  answers: QuizAnswers;
};

export function mapQuestionsToAnswersObject(
  questions: QuizQuestion[],
): QuizAnswers {
  return questions
    .map((question) => question.id)
    .reduce(
      (answers, question) => ({
        ...answers,
        [question]: null,
      }),
      {},
    );
}

function calculateNumberOfCorrectAnswers(
  questions: QuizMachineContext['questions'],
  answers: QuizAnswers,
): number {
  return questions.filter(
    (question) => answers[question.id] === question.correctAnswerIndex,
  ).length;
}

export const createQuizMachine = (
  questions: QuizMachineContext['questions'],
  createLink: ReturnType<typeof useLinkCreator>,
) =>
  Machine<QuizMachineContext>(
    {
      id: 'quiz',
      initial: 'starting',
      context: {
        currentQuestionIndex: 0,
        questions,
        answers: mapQuestionsToAnswersObject(questions),
      },
      states: {
        starting: {
          id: 'starting',
          initial: 'init',
          entry: 'cleanScoreBoard',
          states: {
            init: {
              on: {
                START_QUIZ: {
                  target: '#question',
                  actions: 'goToFirstQuestion',
                },
                CANCEL: 'cancelling',
              },
            },
            cancelling: {
              on: {
                CANCEL: {
                  type: 'final',
                  actions: 'goBack',
                },
                CONTINUE: 'init',
              },
            },
          },
        },
        question: {
          id: 'question',
          initial: 'answering',
          states: {
            answering: {
              on: {
                CANCEL: 'cancelling',
                ANSWER: {
                  actions: 'selectAnswer',
                },
                NEXT_QUESTION: [
                  {
                    target: '#completed',
                    cond: 'canQuizBeCompleted',
                  },
                  {
                    target: 'answering',
                    actions: 'goToNextQuestion',
                    cond: 'isCurrentQuestionAnswered',
                  },
                ],
              },
            },
            cancelling: {
              on: {
                CANCEL: {
                  type: 'final',
                  actions: 'goBack',
                },
                CONTINUE: 'answering',
              },
            },
          },
        },
        completed: {
          id: 'completed',
          type: 'final',
          entry: 'goToQuizResults',
        },
      },
    },
    {
      guards: {
        isCurrentQuestionAnswered: (context) =>
          context.answers[
            context.questions[context.currentQuestionIndex].id
          ] !== null,

        canQuizBeCompleted: (context) =>
          context.currentQuestionIndex + 1 === context.questions.length &&
          context.answers[
            context.questions[context.currentQuestionIndex].id
          ] !== null,
      },
      actions: {
        goToFirstQuestion: assign({
          currentQuestionIndex: (_context) => 0,
        }),
        goToNextQuestion: assign({
          currentQuestionIndex: (context) => context.currentQuestionIndex + 1,
        }),
        selectAnswer: assign({
          answers: (context, event) => ({
            ...context.answers,
            [context.questions[context.currentQuestionIndex].id]:
              event.answerIndex,
          }),
        }),
        cleanScoreBoard: assign({
          answers: (context) => mapQuestionsToAnswersObject(context.questions),
          currentQuestionIndex: (_context) => 0,
        }),
        goBack: () => navigate(createLink(menuLinks.education)),
        goToQuizResults: (context) => {
          const score = calculateNumberOfCorrectAnswers(
            context.questions,
            context.answers,
          );

          const queryString = qs.stringify({ score }, { addQueryPrefix: true });

          navigate(createLink(`/knowledge/quiz-results${queryString}`));
        },
      },
    },
  );
